// src/pages/admin/AdminOrders.jsx
import React, { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Search, Filter, Download, Loader2 } from 'lucide-react';
import { supabase } from '../../services/supabase';

const OrdersStyles = () => (
  <style>{`
    .page-header-flex {
      display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px;
    }
    .page-title { font-size: 1.5rem; font-weight: 700; color: var(--saas-text-primary); margin: 0; }
    
    .btn-primary {
      background: var(--saas-primary); color: white; border: none; padding: 10px 20px;
      border-radius: 6px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
      transition: background 0.2s;
    }
    .btn-primary:hover { background: #245F48; }
    .btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
    
    .btn-outline {
      background: white; color: var(--saas-text-primary); border: 1px solid var(--saas-border-color);
      padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 8px;
      transition: background 0.2s;
    }
    .btn-outline:hover { background: var(--saas-primary-light); }

    .toolbar-card {
      background: white; padding: 16px; border-radius: var(--saas-radius) var(--saas-radius) 0 0;
      border: 1px solid var(--saas-border-color); border-bottom: none;
      display: flex; justify-content: space-between; flex-wrap: wrap; gap: 16px;
    }
    .search-box-clean {
      position: relative; width: 320px;
    }
    .search-input-clean {
      width: 100%; padding: 10px 10px 10px 42px;
      border: 1px solid var(--saas-border-color); border-radius: 6px;
      background: white; color: var(--saas-text-primary); outline: none;
      transition: border-color 0.2s;
    }
    .search-input-clean:focus { border-color: var(--saas-primary); }
    .search-icon { position: absolute; left: 12px; top: 12px; color: var(--saas-text-secondary); }

    .table-container {
      background: white; border-radius: 0 0 var(--saas-radius) var(--saas-radius);
      border: 1px solid var(--saas-border-color); overflow-x: auto;
    }
    .saas-table { width: 100%; border-collapse: collapse; background: white; }
    
    .saas-table th {
      text-align: left; padding: 16px 24px;
      border-bottom: 1px solid var(--saas-border-color);
      color: var(--saas-text-secondary); font-size: 0.75rem; text-transform: uppercase; font-weight: 700; letter-spacing: 0.8px;
      white-space: nowrap; background: white;
    }
    .saas-table td {
      padding: 16px 24px; border-bottom: 1px solid var(--saas-border-color);
      color: var(--saas-text-primary); font-size: 0.9rem; background: white;
    }
    
    /* 整行可點擊 */
    .order-row {
      cursor: pointer;
      transition: box-shadow 0.2s;
    }
    .order-row:hover td {
      background-color: var(--saas-primary-light);
    }
    .order-id { font-family: monospace; font-weight: 600; color: var(--saas-text-primary); }
    .customer-name { font-weight: 600; }
    .amount { font-weight: 700; }

    .status-pill {
      display: inline-flex; align-items: center; padding: 4px 12px;
      border-radius: 9999px; font-size: 0.75rem; font-weight: 700; line-height: 1;
    }
    .pill-paid { background: #f0fdf4; color: #15803d; }
    .pill-confirmed { background: #dbeafe; color: #1e40af; }
    .pill-preparing { background: #fef3c7; color: #92400e; }
    .pill-delivering { background: #e0e7ff; color: #4338ca; }
    .pill-completed { background: #dcfce7; color: #166534; }
    .pill-cancelled { background: #fef2f2; color: #b91c1c; }
    .pill-pending { background: #fff7ed; color: #c2410c; }


    .loading-state, .empty-state {
      padding: 60px 20px; text-align: center; color: var(--saas-text-secondary);
      background: white;
    }
    .loading-spinner {
      animation: spin 1s linear infinite; margin: 0 auto 16px;
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .filter-dropdown { position: relative; display: inline-block; }
    .filter-menu {
      position: absolute; top: 100%; right: 0; margin-top: 8px;
      background: white; border: 1px solid var(--saas-border-color);
      border-radius: 6px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
      padding: 8px; min-width: 180px; z-index: 10;
    }
    .filter-option {
      padding: 8px 12px; cursor: pointer; border-radius: 4px;
      font-size: 0.9rem; transition: background 0.2s;
    }
    .filter-option:hover { background: var(--saas-primary-light); }
    .filter-option.active { background: #d1fae5; color: var(--saas-primary); font-weight: 600; }
  `}</style>
);

const AdminOrders = () => {
  const { orderRefreshTrigger } = useOutletContext() || {};
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOrders();
  }, []);

  // Realtime 觸發時自動重新載入訂單
  useEffect(() => {
    if (orderRefreshTrigger > 0) {
      fetchOrders();
    }
  }, [orderRefreshTrigger]);

  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setOrders(data || []);
    } catch (error) {
      alert('無法載入訂單資料：' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredOrders = orders.filter(order => {
    if (statusFilter !== 'all' && order.payment_status !== statusFilter) {
      return false;
    }

    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const orderNumber = (order.order_number || '').toLowerCase();
      const customerName = (order.contact_info?.name || '').toLowerCase();
      const customerEmail = (order.contact_info?.email || '').toLowerCase();
      
      return orderNumber.includes(searchLower) || 
             customerName.includes(searchLower) || 
             customerEmail.includes(searchLower);
    }

    return true;
  });

  const handleExport = () => {
    if (filteredOrders.length === 0) {
      alert('沒有訂單可以匯出');
      return;
    }

    const headers = ['訂單編號', '客戶姓名', 'Email', '日期', '總金額', '付款狀態'];
    const rows = filteredOrders.map(order => [
      order.order_number,
      order.contact_info?.name || '-',
      order.contact_info?.email || '-',
      new Date(order.created_at).toLocaleDateString('zh-TW'),
      `$${order.total_amount?.toFixed(2) || '0.00'}`,
      getStatusText(order.payment_status)
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `orders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const getStatusText = (paymentStatus) => {
    const statusMap = {
      'paid': '已付款',
      'pending': '待付款',
      'refunded': '已退款',
      'failed': '付款失敗'
    };
    return statusMap[paymentStatus] || paymentStatus;
  };

  return (
    <>
      <OrdersStyles />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button className="btn-primary" onClick={handleExport} disabled={isLoading}>
          <Download size={18} /> 匯出報表
        </button>
      </div>

      <div className="toolbar-card">
        <div className="search-box-clean">
          <Search size={18} className="search-icon" />
          <input 
            type="text" 
            className="search-input-clean" 
            placeholder="搜尋訂單編號、客戶姓名或 Email..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-dropdown">
          <button 
            className="btn-outline"
            onClick={() => setShowFilterMenu(!showFilterMenu)}
          >
            <Filter size={16} /> 
            篩選狀態 {statusFilter !== 'all' && `(${getStatusText(statusFilter)})`}
          </button>
          
          {showFilterMenu && (
            <div className="filter-menu">
              <div 
                className={`filter-option ${statusFilter === 'all' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('all'); setShowFilterMenu(false); }}
              >
                全部
              </div>
              <div 
                className={`filter-option ${statusFilter === 'paid' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('paid'); setShowFilterMenu(false); }}
              >
                已付款
              </div>
              <div 
                className={`filter-option ${statusFilter === 'pending' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('pending'); setShowFilterMenu(false); }}
              >
                待付款
              </div>
              <div 
                className={`filter-option ${statusFilter === 'refunded' ? 'active' : ''}`}
                onClick={() => { setStatusFilter('refunded'); setShowFilterMenu(false); }}
              >
                已退款
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="table-container">
        {isLoading ? (
          <div className="loading-state">
            <Loader2 size={40} className="loading-spinner" />
            <p>正在載入訂單...</p>
          </div>
        ) : filteredOrders.length === 0 ? (
          <div className="empty-state">
            <p>沒有找到符合條件的訂單</p>
          </div>
        ) : (
          <table className="saas-table">
            <thead>
              <tr>
                <th>模式</th>
                <th>方式</th>
                <th>訂單編號</th>
                <th>客戶資訊</th>
                <th>訂購日期</th>
                <th>總金額</th>
                <th>付款狀態</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.map(order => (
                <tr
                  key={order.id}
                  className="order-row"
                  onClick={() => navigate(`/admin/orders/${order.id}`)}
                >
                  <td>{order.booking_mode === 'single' ? '單次點餐' : '週餐訂閱'}</td>
                  <td>{order.delivery_info?.order_type === 'pickup' ? '自取' : '配送'}</td>
                  <td><span className="order-id">{order.order_number}</span></td>
                  <td>
                    <div className="customer-name">{order.contact_info?.name || '-'}</div>
                    <div style={{fontSize: '0.8rem', color: 'var(--saas-text-secondary)'}}>
                      {order.contact_info?.email || '-'}
                    </div>
                  </td>
                  <td style={{color: 'var(--saas-text-secondary)'}}>
                    {new Date(order.created_at).toLocaleDateString('zh-TW', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </td>
                  <td><span className="amount">${order.total_amount?.toFixed(2) || '0.00'}</span></td>
                  <td>
                    <span className={`status-pill pill-${order.payment_status}`}>
                      {getStatusText(order.payment_status)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
};

export default AdminOrders;